import Link from 'next/link';
import ContentStackImage from '@/components/customercenter-ui/common/contentStackImage/contentStackImage';
import ProtectionIcon from '@/components/customercenter-ui/icons/protection';
import NoImage from '@/components/customercenter-ui/svgs/noImage';
import { formatCurrency, formatDateMoment } from '@/helpers/utils/siteUtils';
import {
  DATE_FORMAT,
  DATE_MONTH_FORMAT,
  CANCEL_TYPE,
  PRODUCT_STATUS_CODE,
  STATUS_FRIEGHT,
  STATUS_REFUND,
} from '../../../../../../constants/constant';
import { CANCEL_ORDER_EVENT_TYPES } from '../../../cart/constants';
import CancelOrderModal from '../../../modals/orderDetails/cancelOrder';
import Personalization from '../../../modals/orderDetails/personalization';
import { CardContent } from '../../../ui/card';
import { Item } from '../../types';
import { addFromOrderParam, getCancelReason, getProductStatus } from '../../util';
interface OrderItemsProps {
  Texts: { [key: string]: string };
  item: Item;
  getUpdatedData?: () => void;
}
const AdjustItems = ({ Texts, item, getUpdatedData }: OrderItemsProps) => {
  const formatStatusDescription = (statusDescription: string) => {
    const regex = /\b\d{4}-\d{2}-\d{2}\b/;
    const match = statusDescription?.match(regex);
    const dateInDescription = match ? match[0] : null;
    if (dateInDescription) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const [year, month, day] = dateInDescription.split('-');
      const monthNames = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
      ];
      const date = new Date(dateInDescription);
      return statusDescription.replace(dateInDescription, `${monthNames[date.getMonth()]} ${day}, ${year}`);
    } else return statusDescription;
  };
  const status = getProductStatus(item?.statusCode);
  const statusDescription = formatStatusDescription(item?.statusDesc);
  return (
    <CardContent className="p-0">
      <section className="mb-3 border-b border-[#D9D9D9] pb-2">
        <section className="flex justify-between text-14">
          <div className="mt-4 w-[11%]">{formatDateMoment(item?.date ?? '', DATE_FORMAT)}</div>
          <div className="w-[36%] p-2">
            <div className="flex items-start gap-4">
              <section className="w-[100px]">
                {item?.productInfo?.legacyStyle === STATUS_REFUND ||
                item?.productInfo?.legacyStyle === STATUS_FRIEGHT ? (
                  <img src={'/images/mailOrderNotFound.png'} alt="Product Image" className="h-[90px] w-[90px]" />
                ) : item?.pdpUrl ? (
                  <Link
                    prefetch={false}
                    href={addFromOrderParam(item?.pdpUrl || '')}
                    passHref
                    title={item?.productInfo?.productName ?? ''}
                  >
                    {item?.imageUrl ? (
                      <ContentStackImage
                        className="w-full"
                        src={item?.imageUrl}
                        imageWidth={90}
                        imageHeight={90}
                        alt={`Product Image ${item?.sequence}`}
                        loading="eager"
                      />
                    ) : (
                      <NoImage className="h-[90px] w-[90px]" />
                    )}
                  </Link>
                ) : item?.imageUrl ? (
                  <img src={item?.imageUrl} alt="Product Image" className="h-[90px] w-[90px]" />
                ) : (
                  <NoImage className="h-[90px] w-[90px]" />
                )}
              </section>

              <div>
                {item?.pdpUrl ? (
                  <Link
                    prefetch={false}
                    href={addFromOrderParam(item?.pdpUrl || '')}
                    passHref
                    title={item?.productInfo?.productName ?? ''}
                  >
                    <p className="text-14 font-semibold text-black hover:underline">{item?.productInfo?.productName}</p>
                  </Link>
                ) : (
                  <p className="text-14 font-semibold text-black">{item?.productInfo?.productName}</p>
                )}
                <p>
                  {Texts.itemNumber}: {item?.productInfo?.legacyStyle}
                </p>
                <p>
                  {item?.productInfo?.color} {item?.productInfo?.size ? <>| {item?.productInfo?.size}</> : <></>}
                </p>
              </div>
            </div>
          </div>
          <div className="w-1/6 p-2">
            <p>
              {Texts.ordered}: {item?.orderQuantity}
            </p>
            <p>
              {Texts.Shipped}: {item?.shipQuantity}
            </p>
            <p>
              {Texts.Backordered}: {item?.backorderQuantity}
            </p>
          </div>
          <div className="w-1/6 p-2">
            <p className="text-14 text-[#298842]">{item?.warrantyStatus}</p>
            <section>
              <div>
                {statusDescription !== 'Adjustment' ? (
                  <p className={statusDescription && item?.statusCode === 'BAK' ? 'text-[#298842]' : ''}>
                    {statusDescription}
                  </p>
                ) : (
                  <></>
                )}
                <p className={`text-${status?.color} font-medium`}>{status?.status}</p>
                {item?.fulfillmentMethod === 'vendor' && <p className="text-scarletRed">{Texts.vendorWarning}</p>}
                {item?.fulfillmentMethod && (
                  <p>
                    {Texts.fulfilledBy}: {item?.fulfillmentMethod}
                  </p>
                )}
              </div>

              {item?.postSubmissionInfo?.isCanceled && item?.statusCode === PRODUCT_STATUS_CODE.CANCELLED ? (
                <section className="flex flex-col">
                  <p>
                    {Texts.cancelledOn}: {formatDateMoment(item?.postSubmissionInfo?.canceledOn, DATE_MONTH_FORMAT)}
                  </p>
                  <p>
                    {Texts.reason}: {getCancelReason(item?.postSubmissionInfo?.cancelReason)}
                  </p>
                </section>
              ) : (
                <></>
              )}
              {item?.postSubmissionInfo?.isReturned && item?.statusCode === PRODUCT_STATUS_CODE.RETURN ? (
                <section className="flex flex-col">
                  <p>
                    {Texts.returnedOn}: {formatDateMoment(item?.postSubmissionInfo?.returnedOn, DATE_MONTH_FORMAT)}
                  </p>
                </section>
              ) : (
                <></>
              )}
            </section>
          </div>
          <div className="w-1/6 p-2">
            <p className="text-black">{formatCurrency(item?.itemPriceInfo?.unitPrice)}</p>
          </div>
          <div className="w-1/6 p-2">
            <div className="flex flex-col">
              <div className="font-normal">{formatCurrency(item?.itemPriceInfo?.unitPrice)}</div>
            </div>
            {item?.showCancelItemLink && (
              <CancelOrderModal
                Texts={Texts}
                cancelType={CANCEL_TYPE.ITEM}
                showOptions
                CancelEvent={CANCEL_ORDER_EVENT_TYPES.LINE_ITEM}
                OrderLines={item?.lineSequence}
                className={'text-blueLight underline'}
                getUpdatedData={getUpdatedData}
              />
            )}
          </div>
        </section>
        <section className="flex items-center gap-4">
          <section className="w-24"></section>
          <section className="w-1/2">
            {item?.showProtectionPlan && (
              <section className="border-radius-[6px] rounded-md border-[2px] border-foggyGray bg-lightSilver p-3 shadow-md">
                <section className="flex flex-row gap-4">
                  <section>
                    <ProtectionIcon />
                  </section>
                  <section>
                    <p className="text-sm">
                      <strong>{item?.protectionPlanInfo?.itemName}:</strong> ${item?.protectionPlanInfo?.price}
                    </p>
                  </section>
                </section>
              </section>
            )}
          </section>
          <section className="w-1/2">
            {item?.showPersonalizationLink && <Personalization personalizationInfo={item?.personalizationInfo} />}
          </section>
        </section>
      </section>
    </CardContent>
  );
};

export default AdjustItems;
